import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Camera,
  ClampToEdgeWrapping,
  Frustum,
  Group,
  InterleavedBuffer,
  InterleavedBufferAttribute,
  Intersection,
  LinearFilter,
  LinearMipmapLinearFilter,
  MathUtils,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  Raycaster,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { toPointPositions } from '../../../utilities/pick-near-cursor';
import { RequestWorkerPool } from '../../../workers/request-worker-pool';
import { FetchTiles3tzWorker } from '../../../workers/worker-imports';
import { Tiled3dModel } from '../type';
import { lookupEntry } from './archive';
import { select3tzTiles, Tiles3tzView } from './select-3tz-tiles';
import { tileKey, Tiles3tzTile, Tiles3tzTree } from './tiles-3tz-tree';
import {
  DecodedTile,
  TileCoordinates,
  Tiles3tzArchive,
  Tiles3tzSubtree,
  Tiles3tzWorkerRequest,
} from './types';

export interface Tiles3tzSettings {
  /**
   * A tile is replaced by its children once its geometric error covers more CSS pixels than this
   * on screen. 16 is the 3D Tiles default, about a texel a pixel, see FORMAT.md.
   */
  errorTarget: number;
  /** Most megabytes of tiles kept on the GPU. The tiles drawn or waited for stay past it. */
  memoryBudgetMb: number;
  /**
   * The finest level loaded, 0 being the root's, so the levels can be compared close up; all
   * levels when undefined.
   */
  maxLevel?: number;
}

/**
 * A computer with few cores or little memory keeps half as many tiles. deviceMemory is only known
 * in Chromium, and at most 8.
 */
export function defaultTiles3tzSettings(
  cores = navigator.hardwareConcurrency || 4,
  memoryGb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
): Tiles3tzSettings {
  const weak = cores <= 4 || (memoryGb !== undefined && memoryGb <= 4);
  return { errorTarget: 16, memoryBudgetMb: weak ? 512 : 1024 };
}

/**
 * Time a frame may spend uploading new tiles' textures, which happens on the main thread, so a
 * burst of loaded tiles doesn't stutter. At least one tile is uploaded a frame, so loading never
 * stops. Calibrated by us, not a setting.
 */
const UPLOAD_MS_PER_FRAME = 4;
/** Tiles above this level stay loaded, so a parent is there to fall back on. */
const KEPT_LEVELS = 3;
const RETRY_DELAYS_MS = [1000, 5000, 20000];

type LoadedTile = {
  tile: Tiles3tzTile;
  /** Holds a mesh per primitive, e.g. the top and the walls. */
  object: Group;
  /** Its textures, with their mipmaps, and its geometry on the GPU. */
  bytes: number;
  /** The frame it was last drawn or waited for in. */
  lastUsed: number;
};

function createTexture(image: ImageBitmap) {
  const texture = new Texture(image);
  texture.flipY = false;
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * A tile's meshes, still quantised: its node's translation and scale de-quantise them. Its
 * textures and their bytes on the GPU, where mipmaps add a third.
 */
function createTileObject(decoded: DecodedTile) {
  const textures = decoded.images.map(createTexture);
  let bytes = decoded.images.reduce((sum, { width, height }) => sum + (width * height * 16) / 3, 0);
  const object = new Group();
  for (const primitive of decoded.primitives) {
    bytes += primitive.positions.byteLength + primitive.uvs.byteLength;
    bytes += primitive.indices.byteLength;
    const geometry = new BufferGeometry();
    const positions = new InterleavedBuffer(primitive.positions, primitive.positionStride);
    geometry.setAttribute('position', new InterleavedBufferAttribute(positions, 3, 0, true));
    geometry.setAttribute('uv', new BufferAttribute(primitive.uvs, 2, true));
    geometry.setIndex(new BufferAttribute(primitive.indices, 1));
    object.add(new Mesh(geometry, new MeshBasicMaterial({ map: textures[primitive.image] })));
  }
  object.position.fromArray(decoded.translation);
  object.scale.fromArray(decoded.scale);
  return { object, textures, bytes };
}

function disposeTileObject(object: Group) {
  object.removeFromParent();
  // Primitives may share a texture
  const textures = new Set<Texture>();
  for (const mesh of object.children as Mesh<BufferGeometry, MeshBasicMaterial>[]) {
    mesh.geometry.dispose();
    mesh.material.dispose();
    if (mesh.material.map) {
      textures.add(mesh.material.map);
    }
  }
  for (const texture of textures) {
    texture.dispose();
    (texture.image as ImageBitmap).close();
  }
}

function createPool() {
  return new RequestWorkerPool<Tiles3tzWorkerRequest>(() => new FetchTiles3tzWorker(), '3tz');
}

/**
 * A textured mesh streamed from a 3tz, a 3D Tiles 1.1 tile set with implicit tiling in one ZIP,
 * see FORMAT.md in skymap-photogrammetry (src/tiles/FORMAT.md). Workers read it with range
 * requests and decode its tiles; each frame picks the tiles to draw, loads the missing ones,
 * uploads a few loaded ones, and unloads the least recently used past the memory budget.
 *
 * It is drawn around the block's origin, in the block's frame: x east, y north, z up.
 */
export class Tiles3tzModel extends Tiled3dModel {
  /** The 3tz shown, for its settings. Set on activate(), cleared on clearCustom(). */
  static current: Tiles3tzModel | undefined;
  /**
   * How much detail every 3tz loads, for organization admins to tune. Static, so the setting holds
   * for the session.
   */
  static settings: Tiles3tzSettings = defaultTiles3tzSettings();

  private pool = createPool();
  private readonly origo = new Vector3();
  private archive?: Tiles3tzArchive;
  private tree = new Tiles3tzTree();
  /** The bounds of the data, which the root tile's square is much larger than. */
  private dataBox = new Box3();
  /** Holds the drawn tiles, whose y-up glTF frame it turns to the block's z-up one. */
  private content = new Group();
  private loaded = new Map<string, LoadedTile>();
  /** Decoded tiles waiting for their upload, still loading until then. */
  private decoded: { tile: Tiles3tzTile; decoded: DecodedTile }[] = [];
  private loadedBytes = 0;
  private loadingTiles = new Set<string>();
  private loadingSubtrees = new Set<string>();
  /** Keyed by tile key, and by `subtree-` and the key for subtrees. */
  private failed = new Map<string, { attempts: number; retryAt: number }>();
  private camera?: Camera;
  private renderer?: WebGLRenderer;
  /** The canvas height in CSS pixels. */
  private screenHeight = 1080;
  private frame = 0;
  /** Set by clearCustom(), until activate() starts it again. */
  private cleared = false;

  /** @param url The 3tz's signed url. */
  constructor(private url: string) {
    super();
    // glTF's (x, y, z) is (east, up, −north): +90° about x turns it to (east, north, up)
    this.content.rotation.x = Math.PI / 2;
    this.add(this.content);
  }

  getType() {
    return '3tz-tiles-renderer' as const;
  }

  /** How many levels the 3tz has, once it is open. */
  get availableLevels(): number | undefined {
    return this.archive?.tileset.root.implicitTiling.availableLevels;
  }

  /**
   * Opens the 3tz and reads its root subtree. Also after clearCustom(), when the scene is shown
   * again, which then starts from the beginning.
   */
  async activate() {
    if (this.cleared) {
      this.pool = createPool();
      this.tree = new Tiles3tzTree();
      this.dataBox.makeEmpty();
      this.failed.clear();
      this.loadingTiles.clear();
      this.loadingSubtrees.clear();
      this.cleared = false;
    }
    // Removed by clear(), with the model's other children
    if (!this.content.parent) {
      this.add(this.content);
    }

    const archive = await this.pool.request<Tiles3tzArchive>({ type: 'open', url: this.url });
    const { root } = archive.tileset;
    if (root.implicitTiling.subdivisionScheme !== 'QUADTREE' || root.refine !== 'REPLACE') {
      throw new Error('Only a quadtree tile set refined by REPLACE is supported');
    }
    this.archive = archive;
    Tiles3tzModel.current = this;
    const transform = root.transform ?? [];
    this.origo.set(transform[12] ?? 0, transform[13] ?? 0, transform[14] ?? 0);

    const rootSubtree = await this.loadSubtree({ level: 0, x: 0, y: 0 });
    if (!this.tree.root || !rootSubtree) {
      throw new Error('The 3tz has no root tile');
    }
    // The root subtree's bottom tiles exist only where there is data
    const bottom = Math.max(...rootSubtree.tiles.map(({ level }) => level));
    for (const tile of rootSubtree.tiles) {
      if (tile.level === bottom) {
        this.dataBox.union(this.tree.get(tile)!.box);
      }
    }
  }

  setCameraAndResolution(camera: Camera, renderer: WebGLRenderer) {
    this.camera = camera;
    this.renderer = renderer;

    // The size is (0, 0) on the first frames, before the canvas is sized
    const size = renderer.getSize(new Vector2());
    this.screenHeight = size.y || window.innerHeight || 720;
  }

  /** Picks the tiles to draw for the camera, loads the missing ones and draws the loaded ones. */
  update() {
    const root = this.tree.root;
    if (!root || !this.camera || this.cleared) {
      return;
    }

    this.frame++;
    this.uploadDecoded();
    const selection = select3tzTiles(root, this.view(this.camera), {
      isLoaded: (tile) => this.loaded.has(tile.key),
      hasFailedForGood: (tile) => this.hasFailedForGood(tile.key),
      hasSubtreeFailedForGood: (root) => this.hasFailedForGood(`subtree-${tileKey(root)}`),
    });

    const drawn = new Set(selection.drawn.map(({ key }) => key));
    for (const loaded of this.loaded.values()) {
      if (drawn.has(loaded.tile.key)) {
        loaded.lastUsed = this.frame;
        if (!loaded.object.parent) {
          this.content.add(loaded.object);
        }
      } else {
        loaded.object.removeFromParent();
      }
    }
    for (const tile of selection.kept) {
      this.loaded.get(tile.key)!.lastUsed = this.frame;
    }

    for (const subtree of selection.subtrees) {
      if (this.mayLoad(`subtree-${tileKey(subtree)}`)) {
        void this.loadSubtree(subtree);
      }
    }
    for (const tile of selection.wanted) {
      // Tiles waiting for their upload count, so downloads don't run ahead of the uploads
      if (this.pool.activeRequests + this.decoded.length >= this.pool.capacity) {
        break;
      }
      if (this.mayLoad(tile.key)) {
        void this.loadTile(tile);
      }
    }
    this.unloadUnused();
  }

  /** The camera in the block's frame. */
  private view(camera: Camera): Tiles3tzView {
    this.updateWorldMatrix(true, false);
    const frustum = new Frustum().setFromProjectionMatrix(
      new Matrix4()
        .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
        .multiply(this.matrixWorld),
    );

    if ((camera as OrthographicCamera).isOrthographicCamera) {
      const { top, bottom, zoom } = camera as OrthographicCamera;
      return {
        frustum,
        pixelsPerMetre: (this.screenHeight * zoom) / (top - bottom),
        errorTarget: Tiles3tzModel.settings.errorTarget,
        maxLevel: Tiles3tzModel.settings.maxLevel,
      };
    }

    const fov = MathUtils.degToRad((camera as PerspectiveCamera).getEffectiveFOV());
    return {
      frustum,
      cameraPosition: camera
        .getWorldPosition(new Vector3())
        .applyMatrix4(this.matrixWorld.clone().invert()),
      pixelsPerMetre: this.screenHeight / (2 * Math.tan(fov / 2)),
      errorTarget: Tiles3tzModel.settings.errorTarget,
      maxLevel: Tiles3tzModel.settings.maxLevel,
    };
  }

  private mayLoad(key: string) {
    if (this.loaded.has(key) || this.loadingTiles.has(key) || this.loadingSubtrees.has(key)) {
      return false;
    }
    const failed = this.failed.get(key);
    return !failed || (!this.hasFailedForGood(key) && performance.now() >= failed.retryAt);
  }

  private hasFailedForGood(key: string) {
    return (this.failed.get(key)?.attempts ?? 0) > RETRY_DELAYS_MS.length;
  }

  private markFailed(key: string, err: unknown) {
    const attempts = (this.failed.get(key)?.attempts ?? 0) + 1;
    this.failed.set(key, {
      attempts,
      retryAt: performance.now() + (RETRY_DELAYS_MS[attempts - 1] ?? 0),
    });
    console.warn(`Failed to load the 3tz's ${key}`, err);
  }

  /** Reads the subtree into the tree. Only the root subtree's failure is thrown. */
  private async loadSubtree(root: TileCoordinates): Promise<Tiles3tzSubtree | undefined> {
    const archive = this.archive!;
    const { subtreeLevels, subtrees } = archive.tileset.root.implicitTiling;
    const key = `subtree-${tileKey(root)}`;
    const pool = this.pool;
    this.loadingSubtrees.add(key);
    try {
      const path = subtrees.uri
        .replace('{level}', String(root.level))
        .replace('{x}', String(root.x))
        .replace('{y}', String(root.y));
      const headerOffset = lookupEntry(archive.index, path);
      if (headerOffset === undefined) {
        throw new Error(`The 3tz has no ${path}`);
      }
      const subtree = await pool.request<Tiles3tzSubtree>({
        type: 'subtree',
        url: this.url,
        headerOffset,
        subtreeLevels,
        ...root,
      });
      if (!this.isStale(pool)) {
        this.tree.addSubtree(root, subtree);
      }
      return subtree;
    } catch (err) {
      if (root.level === 0) {
        throw err;
      }
      if (!this.isStale(pool)) {
        this.markFailed(key, err);
      }
      return undefined;
    } finally {
      if (!this.isStale(pool)) {
        this.loadingSubtrees.delete(key);
      }
    }
  }

  private async loadTile(tile: Tiles3tzTile) {
    const { byteOffset, byteLength } = tile.content!;
    const pool = this.pool;
    this.loadingTiles.add(tile.key);
    try {
      const decoded = await pool.request<DecodedTile>({
        type: 'tile',
        url: this.url,
        byteOffset,
        byteLength,
      });
      if (this.isStale(pool)) {
        decoded.images.forEach((image) => image.close());
        return;
      }
      this.failed.delete(tile.key);
      this.decoded.push({ tile, decoded });
    } catch (err) {
      if (!this.isStale(pool)) {
        this.loadingTiles.delete(tile.key);
        this.markFailed(tile.key, err);
      }
    }
  }

  /** Whether the request was made before clearCustom(), so its answer is no longer wanted. */
  private isStale(pool: RequestWorkerPool<Tiles3tzWorkerRequest>) {
    return this.cleared || pool !== this.pool;
  }

  /**
   * Uploads decoded tiles' textures until the frame's upload time is spent, so they are drawn
   * without uploading on the frame they're first drawn.
   */
  private uploadDecoded() {
    const start = performance.now();
    let uploaded = 0;
    while (
      this.decoded.length > 0 &&
      (uploaded === 0 || performance.now() - start < UPLOAD_MS_PER_FRAME)
    ) {
      const { tile, decoded } = this.decoded.shift()!;
      const { object, textures, bytes } = createTileObject(decoded);
      textures.forEach((texture) => this.renderer?.initTexture(texture));
      // Fresh, so it isn't the first unloaded before it's drawn
      this.loaded.set(tile.key, { tile, object, bytes, lastUsed: this.frame });
      this.loadedBytes += bytes;
      this.loadingTiles.delete(tile.key);
      uploaded++;
    }
  }

  /** Unloads the least recently used tiles past the memory budget, never those used this frame. */
  private unloadUnused() {
    const budget = Tiles3tzModel.settings.memoryBudgetMb * 1e6;
    if (this.loadedBytes <= budget) {
      return;
    }

    const unused = [...this.loaded.values()]
      .filter(({ tile, lastUsed }) => lastUsed !== this.frame && tile.level >= KEPT_LEVELS)
      .sort((a, b) => a.lastUsed - b.lastUsed);
    for (const { tile, object, bytes } of unused) {
      if (this.loadedBytes <= budget) {
        break;
      }
      disposeTileObject(object);
      this.loaded.delete(tile.key);
      this.loadedBytes -= bytes;
    }
  }

  async loadLodChunks() {
    // Do nothing, update() loads the tiles
  }

  cullLodChunks() {
    // Do nothing, update() picks the tiles
  }

  isLoading(): boolean {
    return this.loadingTiles.size > 0 || this.loadingSubtrees.size > 0;
  }

  /** Every hit of the ray, front-most first. */
  pick(ray: Raycaster): Intersection[] {
    return ray.intersectObject(this.content, true);
  }

  intersect(ray: Raycaster): Intersection[] {
    return toPointPositions(ray, this.pick(ray));
  }

  getBoundingBox(): Box3 {
    this.updateWorldMatrix(true, false);
    return this.dataBox.clone().applyMatrix4(this.matrixWorld);
  }

  getOrigo(): Vector3 {
    return this.origo;
  }

  clearCustom(): void {
    this.cleared = true;
    this.pool.dispose();
    for (const { object } of this.loaded.values()) {
      disposeTileObject(object);
    }
    this.loaded.clear();
    this.loadedBytes = 0;
    for (const { decoded } of this.decoded) {
      decoded.images.forEach((image) => image.close());
    }
    this.decoded = [];
    this.camera = undefined;
    this.renderer = undefined;
    if (Tiles3tzModel.current === this) {
      Tiles3tzModel.current = undefined;
    }
  }
}
